import { baseApi } from 'shared/api/baseApi'
import { ORGANIZATION_TAG } from 'shared/api/tags'
import {
    Organization,
    OrganizationHistory,
    OrganizationRolesAndPermissions,
    RequestNewOrganization,
    RequestOrganizationEditDetail,
    RequestOrganizationGenerateUploadURL,
    RequestOrganizationRolesAndPermissionsParams,
    RequestOrganizationUploadImage,
} from '..'

export const organizationsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getOrganizations: build.query<Organization[], RequestBasicParams>({
            query: (params) => ({
                url: `organizations`,
                method: 'GET',
                params: params,
            }),

            providesTags: [ORGANIZATION_TAG],
        }),

        getOrganization: build.query<Organization, string>({
            query: (organizationId) => ({
                url: `organizations/${organizationId}`,
                method: 'GET',
            }),

            providesTags: [ORGANIZATION_TAG],
        }),

        newOrganization: build.mutation<Organization, RequestNewOrganization>({
            query: (body) => ({
                url: `organizations`,
                method: 'POST',
                body: body,
            }),
            invalidatesTags: [ORGANIZATION_TAG],
        }),

        generateOrganizationUploadUrl: build.mutation<
            ApiResponse<Organization>,
            RequestOrganizationGenerateUploadURL
        >({
            query: (body) => ({
                url: `organizations/${body.organizationId}/photo/generate-upload-url`,
                method: 'POST',
            }),
        }),

        uploadOrganizationImage: build.mutation<
            ApiResponse<Organization>,
            RequestOrganizationUploadImage
        >({
            query: (body) => ({
                url: `organizations/${body.organizationId}/photo`,
                method: 'POST',
                body: { key: body.key },
            }),
            invalidatesTags: [ORGANIZATION_TAG],
        }),

        editOrganizationDetail: build.mutation<
            ApiResponse<Organization>,
            RequestOrganizationEditDetail
        >({
            query: (body) => ({
                url: `organizations/${body.organizationId}`,
                method: 'PATCH',
                body: body.data,
            }),
            invalidatesTags: [ORGANIZATION_TAG],
        }),

        organizationHistory: build.query<Array<OrganizationHistory>, string>({
            query: (organizationId) => ({
                url: `organizations/${organizationId}/history`,
                method: 'GET',
            }),
            providesTags: [ORGANIZATION_TAG],
        }),

        organizationRoleAndPermission: build.query<
            DataWithMeta<OrganizationRolesAndPermissions>,
            RequestOrganizationRolesAndPermissionsParams
        >({
            query: ({ organizationId, ...params }) => ({
                url: `organizations/${organizationId}/users`,
                method: 'GET',
                params,
            }),
            // Transform response to handle both array and paginated formats
            // TODO: Remove this adapter once backend implements proper pagination
            transformResponse: (
                response:
                    | OrganizationRolesAndPermissions[]
                    | DataWithMeta<OrganizationRolesAndPermissions>,
                _meta,
                arg
            ): DataWithMeta<OrganizationRolesAndPermissions> => {
                // If backend returns paginated format, use it directly
                if (!Array.isArray(response) && response.items) {
                    return response
                }

                // Backend returns plain array - apply client-side pagination
                const allItems = Array.isArray(response)
                    ? response
                    : response.items || []
                const page = arg.page || 1
                const limit = arg.limit || 50
                const startIndex = (page - 1) * limit
                const paginatedItems = allItems.slice(
                    startIndex,
                    startIndex + limit
                )

                return {
                    items: paginatedItems,
                    meta: {
                        page,
                        limit,
                        itemCount: allItems.length,
                        pageCount: Math.ceil(allItems.length / limit),
                        hasPreviousPage: page > 1,
                        hasNextPage: startIndex + limit < allItems.length,
                    },
                }
            },
            providesTags: [ORGANIZATION_TAG],
        }),
    }),
})

export const {
    useGetOrganizationQuery,
    useGetOrganizationsQuery,
    useNewOrganizationMutation,
    useGenerateOrganizationUploadUrlMutation,
    useEditOrganizationDetailMutation,
    useOrganizationHistoryQuery,
    useOrganizationRoleAndPermissionQuery,
} = organizationsApi
